import { useEffect, useRef, useState } from "react";
import { Reorder } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import PhotoUploadForm from "./PhotoUploadForm";
import PhotoCard from "./PhotoCard";
import { useConfirmDelete } from "./AlbumItem";

const onApiError = (err) => toast.error(formatApiError(err.response?.data?.detail));

function usePhotoOrder(album) {
  const [photos, setPhotos] = useState([]);
  const ref = useRef([]);

  useEffect(() => {
    const list = album?.photos ?? [];
    setPhotos(list);
    ref.current = list;
  }, [album]);

  const reorder = (list) => {
    setPhotos(list);
    ref.current = list;
  };

  const persist = async () => {
    if (!album) return;
    try {
      await api.post(`/albums/${album.id}/reorder`, { photo_ids: ref.current.map((p) => p.id) });
      toast.success("Order saved");
    } catch (err) {
      onApiError(err);
    }
  };

  return { photos, reorder, persist };
}

export default function PhotoManager({ album, refresh }) {
  const [edits, setEdits] = useState({});
  const del = useConfirmDelete();
  const order = usePhotoOrder(album);

  useEffect(() => setEdits({}), [album?.id]);

  if (!album) {
    return (
      <section className="flex min-h-[300px] items-center justify-center rounded-3xl border border-dashed border-gold/50 bg-white/60 p-10 text-center" data-testid="photo-manager-empty">
        <p className="font-serif text-xl text-burgundy">Create an album to start uploading photos</p>
      </section>
    );
  }

  const editOf = (p) => edits[p.id] || { caption: p.caption, tags: p.tags.join(", ") };
  const setEdit = (p, patch) => setEdits((prev) => ({ ...prev, [p.id]: { ...editOf(p), ...patch } }));

  const savePhoto = async (p) => {
    const e = editOf(p);
    try {
      await api.patch(`/photos/${p.id}`, { caption: e.caption, tags: e.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Photo updated");
      await refresh();
    } catch (err) {
      onApiError(err);
    }
  };

  const removePhoto = async (p) => {
    try {
      await api.delete(`/photos/${p.id}`);
      toast.success("Photo removed");
      del.clear();
      await refresh();
    } catch (err) {
      onApiError(err);
    }
  };

  const count = album.photos.length;

  return (
    <section className="rounded-3xl border border-gold/40 bg-white/85 p-5 shadow-sm backdrop-blur sm:p-7" data-testid="photo-manager">
      <h2 className="font-serif text-xl font-semibold text-burgundy">
        {album.name} <span className="text-sm font-normal text-plum/50">— {count} {count === 1 ? "photo" : "photos"}</span>
      </h2>

      <PhotoUploadForm albumId={album.id} onUploaded={refresh} />

      {count === 0 ? (
        <p className="mt-8 rounded-2xl border border-dashed border-gold/50 px-6 py-12 text-center text-sm text-plum/60" data-testid="album-empty-photos">
          No photos in this album yet — upload your first above.
        </p>
      ) : (
        <>
          <p className="mt-6 text-xs text-plum/50">Drag the handle on any photo to rearrange the album — the order is saved automatically.</p>
          <Reorder.Group as="ul" axis="y" values={order.photos} onReorder={order.reorder} className="mt-3 grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="admin-photo-grid">
            {order.photos.map((p) => (
              <PhotoCard
                key={p.id}
                p={p}
                edit={editOf(p)}
                onEdit={(patch) => setEdit(p, patch)}
                onSave={() => savePhoto(p)}
                onRemove={() => removePhoto(p)}
                confirmDelete={del.confirmId === p.id}
                onAskDelete={() => del.ask(p.id)}
                onDragEndPersist={order.persist}
              />
            ))}
          </Reorder.Group>
        </>
      )}
    </section>
  );
}
